import { current, type Draft } from 'immer';
import type { CanvasState, HistoryEntry } from './types';

export const HISTORY_LIMIT = 100;

export let isDebugLogging = false;
export function setCanvasDebugLogging(enabled: boolean) {
  isDebugLogging = enabled;
}

export function debugLog(action: string, payload?: unknown) {
  if (!isDebugLogging) return;
  console.log(`[canvas-store] ${action}`, payload ?? null);
}

export function deepClone<T>(value: T): T {
  try {
    if (typeof structuredClone === 'function') {
      // @ts-expect-error structuredClone is available in modern runtimes
      return structuredClone(value);
    }
  } catch {
    /* noop */
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

export function pushHistory(draft: Draft<CanvasState>) {
  const nodes = current(draft.graph.nodes);
  const edges = current(draft.graph.edges);
  const entry: HistoryEntry = { nodes: deepClone(nodes), edges: deepClone(edges) };
  draft.ui.history.past.push(entry);
  if (draft.ui.history.past.length > HISTORY_LIMIT) draft.ui.history.past.shift();
  draft.ui.history.future = [];
}

export function bumpStructureVersion(draft: Draft<CanvasState>) {
  draft.graph.structureVersion = (draft.graph.structureVersion ?? 0) + 1;
}

export function cryptoRandom(length = 6) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const alphabetLength = alphabet.length;
  const cryptoObj = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
  const globalCrypto =
    typeof cryptoObj?.getRandomValues === 'function'
      ? (cryptoObj as { getRandomValues: (array: Uint8Array) => void })
      : undefined;

  if (globalCrypto) {
    try {
      const bytes = new Uint8Array(length);
      globalCrypto.getRandomValues(bytes);
      let out = '';
      for (let i = 0; i < length; i += 1) {
        out += alphabet[bytes[i]! % alphabetLength];
      }
      return out;
    } catch {
      /* fall through to Math.random */
    }
  }

  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabetLength)];
  }
  return out;
}

let commandInsertSerial = 0;
export function nextCommandInsertSerial() {
  const serial = commandInsertSerial;
  commandInsertSerial += 1;
  return serial;
}

type PinCandidate = { id: string; type?: any };

export function pickBestInPin(candidates: PinCandidate[] = [], expected: any) {
  const want = expected ?? 'any';
  const exact = candidates.find((pin) => (pin.type ?? 'any') === want);
  if (exact) return exact;
  const anyPin = candidates.find((pin) => (pin.type ?? 'any') === 'any');
  if (anyPin) return anyPin;
  return candidates[0];
}

export function pickBestOutPin(candidates: PinCandidate[] = [], expected: any) {
  const want = expected ?? 'any';
  const exact = candidates.find((pin) => (pin.type ?? 'any') === want);
  if (exact) return exact;
  const anyPin = candidates.find((pin) => (pin.type ?? 'any') === 'any');
  if (anyPin) return anyPin;
  return candidates[0];
}
